/**
 * useHasProAccess Hook
 *
 * A reactive hook that returns true if the user has Pro access.
 * This hook correctly subscribes to BOTH license store AND subscription store,
 * ensuring UI updates immediately when subscription status changes.
 *
 * IMPORTANT: This hook exists to fix the "stale Pro access" bug where components
 * using `useLicenseStore((s) => s.isPro)` wouldn't re-render when subscription
 * state changed (because `isPro()` internally uses `getState()` on the subscription
 * store, which is not reactive).
 *
 * Use this hook instead of:
 * - `useLicenseStore((s) => s.isPro)` (selects method, not value - never triggers re-render)
 * - `useLicenseStore((s) => s.isPro())` (calls method but doesn't subscribe to subscription store)
 *
 * @returns boolean indicating if user has Pro access (trial, subscription, grace period, or dev mode)
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const hasProAccess = useHasProAccess();
 *   return <div>{hasProAccess ? 'Pro' : 'Personal'}</div>;
 * }
 * ```
 */

import { useLicenseStore } from '../store/licenseStore';
import {
  useSubscriptionStore,
  selectIsPro as selectIsSubscriptionPro,
  selectIsInGracePeriod,
} from '../store/subscriptionStore';
import { useAuthStore } from '../store/authStore';
import { getTrialState } from '../services/trialService';
import { isBrowserModePro } from '../../../features/browser-mode/browserModeUtils';

/**
 * Check if this build was started with Pro mode enabled (dev/test only).
 */
const isProBuild = (): boolean => {
  // Only allow VITE_PRO_MODE in dev mode to prevent production accidents
  if (!import.meta.env.DEV) return false;
  return import.meta.env.VITE_PRO_MODE === 'true';
};

/**
 * Reactive hook for Pro access status.
 *
 * This hook subscribes to all relevant state sources:
 * - License store (trial state)
 * - Subscription store (subscription status, grace period)
 * - Auth store (user ID for account-based trial verification)
 *
 * @returns true if user has Pro access, false otherwise
 */
export function useHasProAccess(): boolean {
  // Subscribe to license state for trial info
  const licenseState = useLicenseStore((s) => s.state);
  const licenseMode = useLicenseStore((s) => s.state.mode);

  // Subscribe to auth state for user ID (account-based trial)
  const userId = useAuthStore((s) => s.user?.id);

  // Subscribe to subscription store for subscription status
  const isSubscriptionPro = useSubscriptionStore(selectIsSubscriptionPro);
  const isInGracePeriod = useSubscriptionStore(selectIsInGracePeriod);

  // Browser mode always has Pro features enabled for SQLite showcase
  if (isBrowserModePro()) {
    return true;
  }

  // Check if trial is active (derived from trial dates, not persisted mode)
  // ACCOUNT-BASED: Pass current user ID to verify trial ownership
  const trialActive = getTrialState(licenseState, userId) === 'active';

  // Pro builds in dev mode can run in Pro mode (for testing)
  if (isProBuild() && licenseMode === 'pro') {
    return true;
  }

  return trialActive || isSubscriptionPro || isInGracePeriod;
}

/**
 * Reactive hook for checking if a specific feature is available.
 *
 * This is equivalent to `useHasProAccess()` since all Pro features
 * are currently gated the same way. Use `useProFeature()` hook if
 * you need feature metadata or upgrade prompts.
 *
 * @param _feature The feature to check (currently unused, all features gated same way)
 * @returns true if feature is available, false otherwise
 */
export function useCanUseFeature(_feature: string): boolean {
  return useHasProAccess();
}

export default useHasProAccess;
